import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { shared } from "../styles.js";

/**
 * theme-toggle is a real <button> that flips data-theme on <html>, persists the
 * choice to localStorage, and re-applies on load (R6/S9). It holds no theme state
 * for other components — they re-theme purely through var(--…) (N2).
 */
@customElement("theme-toggle")
export class ThemeToggle extends LitElement {
  static override styles = [
    shared,
    css`
      button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        padding: 0;
        font-size: 15px;
        line-height: 1;
        border-radius: var(--radius);
        border: 1px solid var(--border);
        background: var(--surface-2);
        color: var(--text-dim);
      }
      button:hover {
        border-color: var(--brand);
        color: var(--text);
      }
    `,
  ];

  @state() private theme = document.documentElement.getAttribute("data-theme") ?? "dark";

  private toggle() {
    this.theme = this.theme === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", this.theme);
    try {
      localStorage.setItem("theme", this.theme);
    } catch {
      /* storage may be unavailable; the in-memory toggle still works */
    }
  }

  override render() {
    const next = this.theme === "dark" ? "light" : "dark";
    return html`<button
      class="btn"
      @click=${this.toggle}
      aria-label=${`Switch to ${next} theme`}
      title=${`Switch to ${next} theme`}
    >
      ${this.theme === "dark" ? "☾" : "☀"}
    </button>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "theme-toggle": ThemeToggle;
  }
}
